import { useRef, useState } from 'react';
import { useMotion, useScrollExpand } from '@/hooks/useEditorialMotion';
import { Link } from 'react-router-dom';
import SubpageShell from '@/components/feature/SubpageShell';
import BookingCta from '@/components/feature/BookingCta';
import { looks, lookDetails, lookSlugs, workFilters } from '@/mocks/home';

const items = looks.map((l, i) => ({ ...l, slug: lookSlugs[i], ...(lookDetails.find((d) => d.title === l.title) ?? { category: 'Cut', service: 'Cut & Style', desc: '' }) }));

export default function WorkPage() {
  const [filter, setFilter] = useState('All');
  const shown = filter === 'All' ? items : items.filter((i) => i.category === filter);
  const gridRef = useRef<HTMLDivElement>(null);
  useScrollExpand(gridRef, [filter]);
  useMotion(gridRef, [filter]);

  return (
    <SubpageShell title="Selected Work" description="A collection of cuts, colours and textures that reflect our approach to hair.">
      <section className="bg-background-50 pb-16 md:pb-28">
        <div className="max-w-[1320px] mx-auto px-5 md:px-12 lg:px-16">
          <div className="flex flex-wrap items-center justify-between gap-4 border-y border-background-300 py-4 mb-10 md:mb-14">
            <div className="flex flex-wrap gap-x-7 gap-y-2" role="tablist">
              {workFilters.map((f) => (
                <button key={f} type="button" role="tab" aria-selected={filter === f} onClick={() => setFilter(f)} className={`text-[14px] font-medium pb-1 border-b whitespace-nowrap cursor-pointer transition-colors ${filter === f ? 'text-foreground-950 border-foreground-950' : 'text-foreground-600 border-transparent hover:text-foreground-950'}`}>{f}</button>
              ))}
            </div>
            <p className="text-[13px] text-foreground-600">{shown.length} looks · Images are illustrative of our style, not client results.</p>
          </div>
          <div ref={gridRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-12">
            {shown.map((l) => (
              <figure key={l.title} data-group data-m="card">
                <Link to={`/work/${l.slug}`} data-expand className="block w-full aspect-[4/5] overflow-hidden bg-background-50 cursor-pointer">
                  <img src={l.src} alt={`${l.title} hairstyle — ${l.tag}`} title={`${l.title} — ${l.tag}`} width={800} height={1000} loading="lazy" className="w-full h-full object-cover object-top" />
                </Link>
                <figcaption className="mt-4">
                  <h2 className="text-[18px] font-semibold text-foreground-950"><Link to={`/work/${l.slug}`} className="cursor-pointer hover:text-foreground-600 transition-colors">{l.title}</Link></h2>
                  <p className="mt-1 text-[14px] text-foreground-600">{l.tag}</p>
                  <p className="mt-2 text-[14px] leading-relaxed text-foreground-700">{l.desc}</p>
                  <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2">
                  <Link to={`/work/${l.slug}`} className="inline-flex items-center gap-1.5 text-[13px] font-medium text-foreground-950 border-b border-foreground-950 pb-0.5 whitespace-nowrap cursor-pointer hover:text-foreground-600">
                    View details <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-line"></i></span>
                  </Link>
                  <Link to={l.service === 'Creative Colour' ? '/services#colour' : '/services#cut'} className=" inline-flex items-center gap-1.5 text-[13px] font-medium text-foreground-950 border-b border-foreground-950 pb-0.5 whitespace-nowrap cursor-pointer hover:text-foreground-600">
                    {l.service} pricing <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-line"></i></span>
                  </Link>
                  </div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>
      <BookingCta title="Inspired by a look?" text="Bring a reference to your consultation and we'll plan a version that suits your hair." />
    </SubpageShell>
  );
}